import type { Language } from "@vektor/shared";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { messages, type MessageKey } from "../translations";

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: MessageKey) => string;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

function initialLanguage(): Language {
  const saved = localStorage.getItem("vektor-language");
  if (saved === "uz" || saved === "ru") return saved;
  return window.Telegram?.WebApp.initDataUnsafe.user?.language_code?.startsWith(
    "ru",
  )
    ? "ru"
    : "uz";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, updateLanguage] = useState<Language>(initialLanguage);
  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage: (next) => {
        localStorage.setItem("vektor-language", next);
        updateLanguage(next);
      },
      t: (key) => messages[language][key],
    }),
    [language],
  );
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const value = useContext(LanguageContext);
  if (!value) throw new Error("LanguageProvider is missing");
  return value;
}
